/**
 * /vs/{slug} — competitor comparison pages.
 *
 * SEO play: each competitor has a static, citation-backed comparison
 * page. We publish our price up-front and call out where competitors
 * hide theirs (with a citation to the source we got the leaked number
 * from). The structured table makes NestPlot-vs-{competitor} googleable.
 *
 * Routing: src/App.tsx registers `<Route path="vs/:slug" element={<VsPage />} />`
 * under the MarketingShell.
 */
import { Link, useParams, Navigate } from "react-router-dom";

import { Btn } from "../../lib/Btn";
import { Card, SectionHeader } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { breadcrumbList, useSeo } from "../../lib/useSeo";

import { COMPETITORS, findCompetitor } from "./data/competitors";
import { STATE_BY_SLUG } from "./data/stateRules";

export function VsPage() {
  const { slug, stateSlug } = useParams<{ slug: string; stateSlug?: string }>();
  const competitor = findCompetitor(slug ?? "");
  const state = stateSlug ? STATE_BY_SLUG[stateSlug] : undefined;
  const path = competitor
    ? state
      ? `/vs/${competitor.slug}/${state.slug}`
      : `/vs/${competitor.slug}`
    : "/vs";

  useSeo(
    competitor
      ? {
          title: state
            ? `NestPlot vs ${competitor.display_name} in ${state.name} — flat from $59`
            : competitor.meta_title,
          description: state
            ? `Looking at ${competitor.display_name} for a ${state.name} facility? Side-by-side with NestPlot on price, lease compliance, gate integration, and tenant rent flow.`
            : competitor.meta_description,
          path,
          // vs/state variants are thin matrix pages — canonical-collapse
          // them to the parent /vs/{competitor} to avoid duplicate-content
          // penalty. The SEO audit (round 2) flagged this.
          canonicalOverride: state
            ? `${typeof window !== "undefined" ? window.location.origin : "https://nestplot.com"}/vs/${competitor.slug}`
            : undefined,
          jsonLd: breadcrumbList(
            state
              ? [
                  { name: "Home", path: "/" },
                  { name: "Compare", path: "/vs" },
                  {
                    name: `NestPlot vs ${competitor.display_name}`,
                    path: `/vs/${competitor.slug}`,
                  },
                  { name: state.name, path },
                ]
              : [
                  { name: "Home", path: "/" },
                  { name: "Compare", path: "/vs" },
                  { name: `NestPlot vs ${competitor.display_name}`, path },
                ],
          ),
        }
      : {
          title: "NestPlot vs the major property management vendors",
          description: "Plain-English head-to-head comparisons against the major multifamily, self-storage, parking, and marina software vendors.",
          path: "/vs",
        },
  );

  if (!competitor) return <Navigate to="/vs" replace />;
  if (stateSlug && !state) return <Navigate to={`/vs/${competitor.slug}`} replace />;

  return (
    <div className="mx-auto max-w-[920px] px-5 lg:px-8 py-10 lg:py-16 space-y-8">
      <div>
        <Link
          to={state ? `/vs/${competitor.slug}` : "/vs"}
          className="text-sub text-[12px] font-mono uppercase tracking-[0.12em] hover:text-ink"
        >
          ← {state ? `NestPlot vs ${competitor.display_name}` : "All comparisons"}
        </Link>
        <h1 className="font-display font-black text-[40px] lg:text-[56px] leading-[0.96] tracking-[-0.04em] mt-3">
          NestPlot vs {competitor.display_name}
          {state && (
            <span className="block text-accent text-[28px] lg:text-[36px] mt-2">
              for {state.name} self-storage operators
            </span>
          )}
        </h1>
        <p className="text-sub text-[16px] mt-3 max-w-[620px]">
          {state
            ? `Looking at ${competitor.display_name} for a facility in ${state.name}? Here's the head-to-head with NestPlot — same pricing, same gate support, same lease engine, plus the ${state.statute}-cited compliance angle.`
            : competitor.one_liner}
        </p>
        {state && (
          <Card className="mt-5">
            <div className="px-5 py-4 bg-accent-soft border-b border-line">
              <div className="font-display font-extrabold text-[14px] uppercase tracking-[-0.005em]">
                Why this matters in {state.name}
              </div>
            </div>
            <div className="px-5 py-4 text-sm text-sub space-y-2">
              <div>
                <strong>Statute:</strong> {state.statute}
              </div>
              <div>
                <strong>Late-fee cap:</strong> ${(state.lateFeeCapCents / 100).toFixed(0)} · <strong>Grace:</strong> {state.gracePeriodDays} days · <strong>Lien-sale notice:</strong> {state.lienSaleNoticeDays} days
              </div>
              <div>
                NestPlot generates a {state.name}-compliant lease from this
                statute on day one. We didn't find a {competitor.display_name}{" "}
                feature that does the same — most rely on a generic template
                you adapt yourself.
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Link to={`/states/${state.slug}`} className="text-accent font-semibold">
                  See full {state.name} rules →
                </Link>
                <Link to={`/tools/late-fee-calculator/${state.slug}`} className="text-accent font-semibold">
                  {state.name} late-fee calculator →
                </Link>
              </div>
            </div>
          </Card>
        )}
      </div>

      <Card>
        <div className="px-6 py-6">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase mb-2">
            The opener
          </div>
          <p className="text-[18px] font-display italic leading-relaxed">
            "{competitor.opener}"
          </p>
        </div>
      </Card>

      <Card>
        <SectionHeader
          title="Pricing — NestPlot publishes, they don't"
          sub="Price transparency is the single biggest differentiator on this page."
        />
        <div className="px-6 py-6 grid md:grid-cols-2 gap-4">
          <div className="border border-ok rounded-md p-5 bg-ok-soft">
            <Pill tone="ok" size="sm">NestPlot</Pill>
            <div className="mt-3 font-display text-[28px] font-black tracking-[-0.02em]">
              From $59/mo
            </div>
            <div className="text-sub text-[13px] mt-1">
              Flat by tier — $59 / $99 / $199. No setup fee. Published right here.
            </div>
          </div>
          <div className="border border-line rounded-md p-5">
            <Pill tone={competitor.competitor_pricing.transparent ? "default" : "warn"} size="sm">
              {competitor.display_name}
            </Pill>
            <div className="mt-3 font-display text-[24px] font-black tracking-[-0.02em]">
              {competitor.competitor_pricing.headline}
            </div>
            <div className="text-sub text-[13px] mt-1">
              {competitor.competitor_pricing.transparent
                ? "Published price"
                : "Not on their site — leaked by operators"}
              {" · "}
              <a
                href={competitor.competitor_pricing.citation}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
              >
                {competitor.competitor_pricing.citation_label}
              </a>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <SectionHeader title="Feature-by-feature" />
        <div className="px-2 py-3">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-[11px] font-mono uppercase tracking-[0.12em] text-sub">
                <th className="text-left py-2 px-4">Feature</th>
                <th className="text-left py-2 px-4">NestPlot</th>
                <th className="text-left py-2 px-4">{competitor.display_name}</th>
              </tr>
            </thead>
            <tbody>
              {competitor.rows.map((r, i) => (
                <tr
                  key={r.label}
                  className={i % 2 === 0 ? "bg-surface" : ""}
                >
                  <td className="py-3 px-4 font-semibold align-top">{r.label}</td>
                  <td className="py-3 px-4 align-top">
                    <span className={r.nestplot_better ? "text-ok font-semibold" : ""}>
                      {r.nestplot_better && (
                        <Icon name="check" size={13} className="inline mr-1" />
                      )}
                      {r.nestplot}
                    </span>
                  </td>
                  <td className="py-3 px-4 align-top text-sub">{r.competitor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <SectionHeader
          title={`What operators say about ${competitor.display_name}`}
          sub="From Capterra, Self-Storage Talk, and G2 reviews"
        />
        <div className="px-6 py-5 space-y-3 text-[13.5px] text-sub">
          {competitor.complaints.map((c) => (
            <div key={c} className="flex gap-2 items-start">
              <Icon name="warning" size={14} className="text-warn mt-[3px] shrink-0" />
              <span>{c}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="px-6 py-6 text-center space-y-4">
          <h2 className="font-display font-black text-[24px] tracking-[-0.02em]">
            Ready to switch?
          </h2>
          <p className="text-sub max-w-[460px] mx-auto text-[14px]">
            Start free for 30 days. No card. No sales call. Migration help
            included for the first 100 tenants.
          </p>
          <div className="flex justify-center gap-3 pt-2">
            <Link to="/signup">
              <Btn kind="primary" size="md" iconRight={<Icon name="arrow-right" size={14} />}>
                Start free trial
              </Btn>
            </Link>
            <Link to="/contact">
              <Btn kind="secondary" size="md">
                Contact sales
              </Btn>
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}

// URL-slug → Competitor.vertical key. Marketing surfaces use a friendlier
// slug (rv-and-boat) than the schema enum (vehicle_storage).
type VsVertical = "multifamily" | "self_storage" | "parking" | "vehicle_storage";

const VS_SLUG_TO_VERTICAL: Record<string, VsVertical> = {
  "self-storage": "self_storage",
  multifamily: "multifamily",
  "rv-and-boat": "vehicle_storage",
  parking: "parking",
};

const VS_VERTICAL_LABEL: Record<VsVertical, string> = {
  multifamily: "Multifamily",
  self_storage: "Self-storage",
  parking: "Parking & lots",
  vehicle_storage: "RV & boat",
};

const VS_VERTICAL_SLUG: Record<VsVertical, string> = {
  multifamily: "multifamily",
  self_storage: "self-storage",
  parking: "parking",
  vehicle_storage: "rv-and-boat",
};

const VS_VERTICAL_ACCENT: Record<VsVertical, string> = {
  multifamily: "var(--multifamily-accent, #3F6B4B)",
  self_storage: "var(--storage-accent, #B5543B)",
  parking: "var(--parking-accent, #6C5AA8)",
  vehicle_storage: "var(--vehicle-accent, #2D6E8F)",
};

const VS_VERTICAL_ORDER: VsVertical[] = [
  "multifamily",
  "self_storage",
  "parking",
  "vehicle_storage",
];

const VS_VERTICAL_BLURB: Record<VsVertical, string> = {
  multifamily:
    "AppFolio, Yardi Breeze, Buildium, RentRedi — head-to-head with the multifamily incumbents on price, payments, and per-unit fees.",
  self_storage:
    "storEDGE, SiteLink, Easy Storage Solutions, Storable — the self-storage software stack vs. flat pricing from $59, 0% of your rent.",
  parking:
    "ParkMobile, Flowbird, Passport Parking — driver-side convenience-fee apps vs. an operator-side stack.",
  vehicle_storage:
    "Marina Office Manager, Speedwell, Molo, Dockwa — marina + RV software comparisons against a single per-site fee.",
};

export function VsIndexPage() {
  const params = useParams();
  const vertical = params.vertical ? VS_SLUG_TO_VERTICAL[params.vertical] : null;
  const label = vertical ? VS_VERTICAL_LABEL[vertical] : null;
  useSeo({
    title: vertical
      ? `Compare ${label!.toLowerCase()} software — NestPlot`
      : "NestPlot vs the major property management vendors",
    description: vertical
      ? `${label} software comparisons against the major incumbents — honest, citation-backed, no marketing fluff. Same vendor integrations, lower price.`
      : "Plain-English head-to-head comparisons of NestPlot vs multifamily, storage, parking, and marina software vendors, organised by vertical. We publish what NestPlot costs. Most of them don't.",
    path: vertical ? `/vs/vertical/${params.vertical}` : "/vs",
  });

  if (vertical) {
    const visible = COMPETITORS.filter((c) => c.vertical === vertical);
    return (
      <div className="mx-auto max-w-[920px] px-5 lg:px-8 py-10 lg:py-16 space-y-6">
        <div>
          <Link
            to="/vs"
            className="text-sub text-[12px] font-mono uppercase tracking-[0.12em] hover:text-ink"
          >
            ← All vertical comparisons
          </Link>
          <h1 className="font-display font-black text-[40px] lg:text-[56px] leading-[0.96] tracking-[-0.04em] mt-3">
            NestPlot vs the {label!.toLowerCase()} incumbents
          </h1>
          <p className="text-sub text-[16px] mt-3 max-w-[620px]">
            {VS_VERTICAL_BLURB[vertical]}
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {visible.map((c) => (
            <Link
              key={c.slug}
              to={`/vs/${c.slug}`}
              className="block border border-line rounded-md p-5 hover:border-line-strong"
            >
              <div className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em]">
                NestPlot vs {c.display_name}
              </div>
              <div className="text-sub text-[13px] mt-2 line-clamp-2">{c.one_liner}</div>
              <div className="text-accent text-[12px] font-mono uppercase tracking-[0.1em] mt-3">
                Compare →
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1080px] px-5 lg:px-8 py-10 lg:py-16">
      <div className="max-w-[720px]">
        <h1 className="font-display font-black text-[40px] lg:text-[56px] leading-[0.96] tracking-[-0.04em]">
          NestPlot vs everyone else
        </h1>
        <p className="text-sub text-[16px] mt-3 max-w-[620px]">
          Side-by-side comparisons with the major property management software
          vendors — organised by vertical. We publish what NestPlot costs. Most
          of them don't.
        </p>
        <nav className="mt-7 flex flex-wrap gap-2 text-sm">
          {VS_VERTICAL_ORDER.map((v) => (
            <a
              key={v}
              href={`#${VS_VERTICAL_SLUG[v]}`}
              className="border border-line rounded-md px-3 py-1.5 font-semibold hover:bg-paper hover:border-line-strong transition-colors"
            >
              {VS_VERTICAL_LABEL[v]} →
            </a>
          ))}
        </nav>
      </div>

      <div className="mt-12 space-y-12">
        {VS_VERTICAL_ORDER.map((v, idx) => {
          const items = COMPETITORS.filter((c) => c.vertical === v);
          if (items.length === 0) return null;
          return (
            <section
              key={v}
              id={VS_VERTICAL_SLUG[v]}
              className={idx > 0 ? "border-t border-line pt-12" : undefined}
            >
              <div className="grid lg:grid-cols-[1fr_2.5fr] gap-6 items-start">
                <div>
                  <div
                    className="font-mono text-[11px] tracking-[0.18em] uppercase"
                    style={{ color: VS_VERTICAL_ACCENT[v] }}
                  >
                    Vertical · {VS_VERTICAL_LABEL[v]}
                  </div>
                  <h2 className="font-display font-extrabold text-[28px] uppercase tracking-[-0.015em] mt-2 leading-tight">
                    {VS_VERTICAL_LABEL[v]} incumbents
                  </h2>
                  <p className="text-sub text-sm mt-3 max-w-[320px]">
                    {VS_VERTICAL_BLURB[v]}
                  </p>
                  <Link
                    to={`/vs/vertical/${VS_VERTICAL_SLUG[v]}`}
                    className="text-accent font-semibold text-sm mt-3 inline-block"
                  >
                    Browse all {VS_VERTICAL_LABEL[v].toLowerCase()} comparisons →
                  </Link>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {items.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/vs/${c.slug}`}
                      className="block border border-line rounded-md p-5 hover:border-line-strong"
                    >
                      <div className="font-display font-extrabold text-[17px] uppercase tracking-[-0.005em]">
                        NestPlot vs {c.display_name}
                      </div>
                      <div className="text-sub text-[13px] mt-2 line-clamp-2">
                        {c.one_liner}
                      </div>
                      <div
                        className="text-[12px] font-mono uppercase tracking-[0.1em] mt-3 font-semibold"
                        style={{ color: VS_VERTICAL_ACCENT[v] }}
                      >
                        Compare →
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
